import { useSchedulerPropsContext } from '../context/schedulerContext'
import {
  CELL_HEIGHT,
  MINUTES_IN_CELL_HEIGHT,
} from '../helpers/constants'
import { useMemo } from 'react'
import { IntervalDates } from '@pogokid/util/datetime'
import {
  calcMinuteHeight,
  clockMinutesBetween,
  minutesFromStartHour,
} from '../helpers/positionCalculations'
import { Optional } from '@pogokid/util'
import { CalendarEvent } from '../types.ts'
import { overlappingEventsWith } from '../helpers/overlappingEventsWith.ts'

export function useMinutesInCell() {
  return MINUTES_IN_CELL_HEIGHT
}

export function useMinuteHeight() {
  const step = useMinutesInCell()
  return useMemo(
    () => calcMinuteHeight(CELL_HEIGHT, step),
    [step]
  )
}

interface PositionedResult {
  event: CalendarEvent
  uid: string
  top: number
  height: number
  width: string
  left: string
}

/**
 * Calculate utils for the absolute position of an event
 * based on schedular settings
 */
export function useEventPositioning() {
  const { week } = useSchedulerPropsContext()
  const minuteHeight = useMinuteHeight()
  function positionEvent(
    range: Optional<IntervalDates>,
    duration?: number
  ) {
    const eventDuration = duration
      ? duration
      : range
      ? clockMinutesBetween(range.start, range.end)
      : 0
    const minutesFromStart = range
      ? minutesFromStartHour(week?.startHour ?? 0, range.start)
      : 0
    // The grid follows the clock, so a lesson that crosses
    // the clocks changing is drawn to the time it ends on
    // the clock, not by how many minutes pass
    const clockDuration =
      duration && range
        ? clockMinutesBetween(
            range.start,
            new Date(range.start.getTime() + duration * 60_000)
          )
        : eventDuration

    return {
      duration: eventDuration,
      top: minutesFromStart * minuteHeight,
      height: clockDuration * minuteHeight,
      minutesFromStart,
    }
  }
  return {
    arrangeEvents: (events: CalendarEvent[]) => {
      const crossingIds = new Set<number | string>()
      const arranged = new Map<
        number | string,
        PositionedResult
      >()
      const resultsSet = new Set<number | string>()
      events.forEach((event) => {
        const { height, top } = positionEvent(
          event,
          event.duration
        )
        const crossingEvents = overlappingEventsWith(
          events,
          event
        )
        const alreadyRendered = crossingEvents.filter((e) =>
          crossingIds.has(e.uid)
        )
        const result: PositionedResult = {
          event,
          uid: event.uid,
          top,
          height,
          width: crossingEvents.length
            ? `${90 / (crossingEvents.length + 1)}%`
            : '90%',
          left:
            alreadyRendered.length > 0
              ? `calc(90%/${alreadyRendered.length + 1} + 5px)`
              : '4px',
        }
        crossingIds.add(event.uid)
        arranged.set(event.uid, result)
        resultsSet.add(event.uid)
      })

      return {
        arranged,
        orderedUids: resultsSet,
      }
    },
    positionEvent,
    positionStartMinute: (
      minutesFromStart: number,
      duration: number
    ) => {
      return {
        top: minutesFromStart * minuteHeight,
        height: duration * minuteHeight,
      }
    },
    sizeEvent: () => {},
  }
}
